import React, { useLayoutEffect, useRef, useState } from 'react';
import { MarcomPaddingProps } from 'styled-system';
import { JoykitColor } from '@components/themes';
import ScrollMagic from 'scrollmagic';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { gsap, Linear, Power1 } from 'gsap';
import { ImgContainer, TextContainer, Wrapper } from './TextBackgroundPhoto.styles';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { defaultBase64Placeholder } from '@components/CloudImage';

type TextBackgroundPhotoProps = Readonly<{
  src: string;
  children: React.ReactNode;
  gradient?: string;
  height?: string;
  zoom?: boolean;
  textCentered?: boolean;
  moveText?: boolean;
  backgroundHeight?: string;
  backgroundColor?: JoykitColor;
  backgroundPosition?: string;
}> &
  MarcomPaddingProps;

export const TextBackgroundPhoto: React.FC<TextBackgroundPhotoProps> = ({
  src,
  gradient,
  children,
  height,
  zoom = false,
  paddingTop = 0,
  textCentered = false,
  moveText = false,
  backgroundHeight,
  backgroundColor,
  backgroundPosition,
}) => {
  const [isAppearedInView, setIsAppearedInView] = useState(false);
  const actualSrc = isAppearedInView ? src : defaultBase64Placeholder;
  const textRef = useRef<HTMLDivElement>(null);
  const textBackgroundPhotoContainerRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let textBackgroundPhotoTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      // Initialize timeline
      const initTimeline = () => {
        if (scrollController) {
          textBackgroundPhotoTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
          textBackgroundPhotoTimeline.fromTo(
            textBackgroundPhotoContainerRef.current,
            { scale: 1, ease: Power1.easeIn },
            { scale: 1.5, ease: Power1.easeIn }
          );
        }
      };

      // Initialize Scene
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#pin', // TODO: Figure out what's a good id to use here.
          triggerHook: 'onCenter',
          duration: '100%',
          offset: 100,
        })
          .addTo(scrollController)
          // TODO: Set pin based off the value of moveText prop. (use textRef here)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            textBackgroundPhotoTimeline?.progress(event.progress);
          });
      };

      // Call init timeline and init scene
      initTimeline();
      initScene();
    }
    // Clear scene / handle necessary cleanup work
    const clearScene = () => {
      if (textBackgroundPhotoTimeline) {
        textBackgroundPhotoTimeline.progress(0);
        textBackgroundPhotoTimeline.kill();
      }

      if (scene) {
        scene.destroy(true);
      }
    };

    return clearScene;
  }, [scrollController, isMounted]);

  return (
    <Wrapper height={height} paddingTop={paddingTop} textCentered={textCentered} id="pin">
      <LazyLoadWrapper
        beforeLoad={() => {
          setIsAppearedInView(true);
        }}
      >
        <div />
      </LazyLoadWrapper>
      {zoom ? (
        <>
          <ImgContainer
            ref={textBackgroundPhotoContainerRef}
            gradient={gradient}
            src={actualSrc}
            backgroundHeight={backgroundHeight}
            backgroundColor={backgroundColor}
            backgroundPosition={backgroundPosition}
          />
          <TextContainer ref={textRef}>{children}</TextContainer>
        </>
      ) : (
        <>
          <TextContainer ref={textRef}>{children}</TextContainer>
          <ImgContainer
            gradient={gradient}
            src={actualSrc}
            backgroundHeight={backgroundHeight}
            backgroundColor={backgroundColor}
            backgroundPosition={backgroundPosition}
          />
        </>
      )}
    </Wrapper>
  );
};
